import posthog from "posthog-js"

import {
  POSTHOG_DO_NOT_RECORD_CLASS,
  tagSensitiveCaptureRoots,
} from "~/product-analytics/ph-no-capture"

const isExplicitlyEnabled = import.meta.env.VITE_POSTHOG_ENABLED === "true"
const isDebug = import.meta.env.VITE_POSTHOG_DEBUG === "true"
const projectToken = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN
const apiHost = import.meta.env.VITE_POSTHOG_HOST
const environment =
  import.meta.env.VITE_POSTHOG_ENVIRONMENT ?? import.meta.env.MODE

const hasRequiredConfig = Boolean(projectToken && apiHost)

export type PosthogDeliveryMode = "full" | "replay-only"

/**
 * Env enablement only. Browser delivery still requires account preferences
 * before {@link ensurePosthogInitialized} runs.
 */
export const isPosthogEnabled = isExplicitlyEnabled && hasRequiredConfig
export const posthogEnvironment = environment

let hasInitialized = false
let activeDeliveryMode: PosthogDeliveryMode | null = null

if (isExplicitlyEnabled && !hasRequiredConfig) {
  const missingVariable = projectToken
    ? "VITE_POSTHOG_HOST"
    : "VITE_POSTHOG_PROJECT_TOKEN"

  throw new Error(
    `PostHog is enabled but ${missingVariable} is missing or un-configured`
  )
}

if (
  !isExplicitlyEnabled &&
  import.meta.env.DEV &&
  import.meta.env.MODE !== "test" &&
  !hasRequiredConfig
) {
  console.warn(
    "PostHog is disabled (VITE_POSTHOG_ENABLED is not true). Browser analytics will be skipped."
  )
}

const buildSessionRecordingConfig = () => ({
  maskAllInputs: true,
  maskTextSelector: `.${POSTHOG_DO_NOT_RECORD_CLASS}`,
  blockSelector: `.${POSTHOG_DO_NOT_RECORD_CLASS}`,
})

/**
 * Lazily initialise the browser SDK only after account preferences allow delivery.
 */
export const ensurePosthogInitialized = (
  mode: PosthogDeliveryMode
): boolean => {
  if (!isPosthogEnabled || !projectToken || !apiHost) {
    return false
  }

  if (hasInitialized) {
    return true
  }

  posthog.init(projectToken, {
    api_host: apiHost,
    defaults: "2026-01-30",
    autocapture: mode === "full",
    capture_pageview: mode === "full",
    disable_session_recording: true,
    opt_out_capturing_by_default: true,
    session_recording: buildSessionRecordingConfig(),
  })

  tagSensitiveCaptureRoots()

  if (isDebug) {
    posthog.debug()
  }

  hasInitialized = true
  activeDeliveryMode = mode
  return true
}

export const isPosthogInitialized = (): boolean => hasInitialized

export const getActivePosthogDeliveryMode = (): PosthogDeliveryMode | null =>
  activeDeliveryMode

/** Test-only: reset the lazy-init latch between specs. */
export const resetPosthogInitializationForTests = (): void => {
  hasInitialized = false
  activeDeliveryMode = null
}

export default posthog
